import type { ApplicationContext, Log } from '@hc-frontend/core-context';
import {
  createSuccessOperationResult,
  type IErrorOperationResult,
  type ISuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';

import { DomainIds } from '../../entities';
import type { TrackingSessionRepository } from '../../repositories';

export type GetDomainIdsUseCase = () => Promise<
  ISuccessOperationResult<Partial<DomainIds>>
>;

export type GetDomainIdsFactoryParams = {
  log: ApplicationContext['log'];
  sessionProvider: TrackingSessionRepository;
};

function logOperation<T extends IErrorOperationResult<string>>(
  log: ApplicationContext['log'],
  result: T,
  type: Log['type'] = 'error',
): T {
  log({
    type,
    context: 'server',
    message: result.errors[0].code,
    data: {
      rootCause: result.errors[0].rootCause,
    },
  });

  return result;
}

export function getDomainIdsFactory({
  log,
  sessionProvider,
}: GetDomainIdsFactoryParams): GetDomainIdsUseCase {
  return async () => {
    const domainIds: Partial<DomainIds> = {};

    const userId = await sessionProvider.getUserId();

    if (!userId.success) {
      logOperation(log, userId, 'warn');
    } else {
      domainIds.userId = userId.data;
    }

    const sessionId = await sessionProvider.getSessionId();

    if (!sessionId.success) {
      logOperation(log, sessionId, 'warn');
    } else {
      domainIds.sessionId = sessionId.data;
    }

    const parsedDomainIds = DomainIds.partial().safeParse(domainIds);

    if (!parsedDomainIds.success) {
      logOperation(log, {
        success: false,
        errors: [
          {
            code: 'failedToParseDomainIds',
            rootCause: new Error(parsedDomainIds.error.message),
          },
        ],
      });
    } else {
      return createSuccessOperationResult(parsedDomainIds.data);
    }

    return createSuccessOperationResult({});
  };
}
